import {Component} from "react";
import "./MySvg.css";
import {arcDataFromPercent, cleanPercentage, parseCircularProps} from "./MySvgShared";
import PropTypes from "prop-types";

const parseValues = ( vals = [] ) => {
	vals = vals.filter( v => parseFloat(v?.value ?? 0) );
	let tot = 0;
	vals.forEach( v => {
		v.value = parseFloat(v.value);
		tot += v.value;
	});
	if ( tot === 0 ) tot = 1;
	vals.forEach( v => { v.percent = (100 * v.value) / tot; });
	return vals;
};

/**
 * @typedef SvgPieValue
 * @type {Object}
 * @property {Number} value
 * @property {string} [color]
 */

/**
 * @desc Create a multi-value Pie chart
 * @version 1.1
 * @type {Component}
 * @property {Array<SvgPieValue>} values
 * @property {Number} [size] Size in px, defaults to 800
 * @property {Number} [startAngle] Starting angle in rads, defaults to 0 (top)
 * @property {Number} [radius] The radius of the pie in px, defaults to size/2
 * @property {Number} [hole] The proportion of the inner hole in a 0..1 range, defaults to 0 (no hole)
 * @property {string} [color] Default color for values, defaults to "#369"
 * @property {string} [bgColor] Pie background color, defaults to "transparent"
 * @property {Number} [border] Pie border size in px, defaults to 1
 * @property {string} [borderColor] Pie border color, defaults to "color" property if border has a width > 0
 * @property {Number} [borderOffset] Border offset in px, defaults to border size with a maximum default value of 2
 * @author [Marco Balestra](https://altersoftware.it)
 * @copyright © 2023 balestra@altersoftware.it
 */
const SvgPie = class extends Component {
	static propTypes = {
		/** @type {Array<SvgPieValue>} */
		values: PropTypes.arrayOf(PropTypes.object).isRequired,
		/**
		 * Size in px, defaults to 800
		 * @type {number}
		 */
		size: PropTypes.number,
		/**
		 * Starting angle in rads, defaults to 0 (top)
		 * @type {number}
		 */
		startAngle: PropTypes.number,
		/**
		 * The radius of the pie in px, defaults to size/2
		 * @type {number}
		 */
		radius: PropTypes.number,
		/**
		 *  The proportion of the inner hole in a 0..1 range, defaults to 0 (no hole)
		 * @type {number}
		 */
		hole: PropTypes.number,
		/**
		 * Default color for values, defaults to "#369"
		 * @type {string}
		 */
		color: PropTypes.string,
		/**
		 * Pie background color, defaults to "transparent"
		 * @type {string}
		 */
		bgColor: PropTypes.string,
		/**
		 * Pie border size in px, defaults to 1
		 * @type {number}
		 */
		border: PropTypes.number,
		/**
		 * Pie border color, defaults to "color" property if border has a width > 0
		 * @type {string}
		 */
		borderColor: PropTypes.string,
		/**
		 * Border offset in px, defaults to border size with a maximum default value of 2
		 * @type {number}
		 */
		borderOffset: PropTypes.number,
	}
	constructor(props) {
		super(props);
		this.state = parseCircularProps(props, {  values: parseValues(props.values ) });
		let lastAngle = this.state.startAngle;
		this.state.values.forEach( v => {
			const sd = arcDataFromPercent(
				this.state.radius,
				this.state.radius,
				this.state.radius - this.state.border - this.state.borderOffset,
				v.percent,
				lastAngle
			);
			lastAngle = sd.angle;
			if ( props.debug ) {
				console.log(v,sd,lastAngle);
			}
			v.d = sd.d;
		});
		const classes = ["SvgPie"];
		if ( this.state.sizeClass ) classes.push("Size"+this.state.sizeClass);
		if ( this.state.maskid ) classes.push("Masked");
		if ( this.props.className ) classes.push(this.props.className);
		this.state.className = classes.join(' ');
	}
	render() {
		return (
			<div
				className={this.state.className}
				data-value={this.state.label || this.state.value}
				title={this.props.title || this.state.label || this.props.value || ""}
				style={{
					width: String(this.state.size)+"px",
					height: String(this.state.size)+"px"
				}}
			>
				<svg
					version="1.1"
					xmlns="http://www.w3.org/2000/svg"
					width={this.state.size}
					height={this.state.size}
					style={{
						width: String(this.state.size)+"px",
						height: String(this.state.size)+"px"
					}}
				>
					{ (!! this.state.maskid ) && this.state.mask }
					<circle
						className={"SvgPieExternal"}
						r={ this.state.radius - this.state.border / 2}
						cx={this.state.radius}
						cy={this.state.radius}
						stroke={this.state.borderColor}
						strokeWidth={this.state.border}
						fill={this.state.bgColor}
						mask={ this.state.maskid ? `url(#${this.state.maskid})` : undefined }
					/>
					{ this.state.values.length > 0 && this.state.values.map( (v,idx) => {
						const t = this.props.labels ? ((v.label ? `${v.label}, ` : "")+
							cleanPercentage(v.percent)+
							(parseFloat(v.value) !== v.percent ? ` (${v.value})` : "")) : false;
						return (
							<path
								key={idx}
								className={"SvgPieInternal"+(v.className ? " "+v.className : "")}
								fill={v.color||undefined}
								d={ v.d }
								mask={ this.state.maskid ? `url(#${this.state.maskid})` : undefined }
							>
								{ t && <title>{t}</title>}
								{ t && <desc>{t}</desc>}
							</path>
						);
					})}
				</svg>
			</div>
		)
	}
}

export default SvgPie;
